import { useEffect, useState, useRef } from "react";
import {
  Image as ImageIcon,
  UploadCloud,
  Search,
  Copy,
  Check,
  Trash2,
  Edit2,
  ExternalLink,
  Info,
  X,
  FileText,
  Filter,
  Eye,
  AlertCircle,
  RotateCcw,
  LayoutGrid,
  List
} from "lucide-react";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { EmptyState } from "../../components/common/EmptyState";
import { apiGet, apiSend, apiUpload } from "../../services/api";
import { formatDate } from "../../utils/date";

export type MediaRecord = {
  id: number;
  filename: string;
  original_filename: string;
  url: string;
  mime_type: string;
  file_size: number;
  width: number;
  height: number;
  alt_text: string | null;
  caption: string | null;
  credit: string | null;
  description: string | null;
  responsive_json?: Record<string, string> | null;
  created_at: string;
};

export function MediaPage() {
  const [items, setItems] = useState<MediaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<MediaRecord | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    alt_text: "",
    caption: "",
    credit: "",
    description: ""
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function loadMedia(query = searchQuery) {
    try {
      setLoading(true);
      setError(null);
      const url = query.trim()
        ? `/api/media?q=${encodeURIComponent(query.trim())}&pageSize=60`
        : "/api/media?pageSize=60";
      const res = await apiGet<{ items: MediaRecord[]; page: number; pageSize: number }>(url);
      setItems(res.items || []);
    } catch (err: any) {
      setError(err.message || "Failed to load media assets");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMedia();
  }, []);

  async function handleFileUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    
    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/avif"];
    if (!validTypes.includes(file.type)) {
      alert("Please upload a supported image format (JPEG, PNG, WebP, AVIF)");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(`Uploading and optimizing ${file.name}...`);
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("alt_text", file.name.replace(/\.[^/.]+$/, ""));

      await apiUpload("/api/media", formData);

      await loadMedia();
      setUploadProgress("Image successfully processed and added to library.");
      setTimeout(() => setUploadProgress(null), 3000);
    } catch (err: any) {
      alert(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Are you sure you want to delete this media asset? It may break articles referencing this image.")) {
      return;
    }
    try {
      await apiSend(`/api/media/${id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (selectedMedia?.id === id) {
        setSelectedMedia(null);
      }
    } catch (err: any) {
      alert(err.message || "Failed to delete media asset");
    }
  }

  function handleOpenDetails(media: MediaRecord) {
    setSelectedMedia(media);
    setEditForm({
      alt_text: media.alt_text || "",
      caption: media.caption || "",
      credit: media.credit || "",
      description: media.description || ""
    });
    setIsEditing(false);
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMedia) return;
    try {
      setSavingEdit(true);
      await apiSend(`/api/media/${selectedMedia.id}`, {
        method: "PATCH",
        body: JSON.stringify(editForm)
      });
      
      // Update local state
      const updated = { ...selectedMedia, ...editForm };
      setSelectedMedia(updated);
      setItems((prev) => prev.map((item) => (item.id === selectedMedia.id ? updated : item)));
      setIsEditing(false);
    } catch (err: any) {
      alert(err.message || "Failed to update media details");
    } finally {
      setSavingEdit(false);
    }
  }

  function copyUrl(url: string, id: number) {
    const fullUrl = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2500);
  }

  function formatBytes(bytes: number) {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  }

  return (
    <div className="admin-page-flow">
      {/* Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <ImageIcon size={13} className="text-[#C99A2E]" />
            <span>EDITORIAL ASSETS</span>
          </div>
          <h1 className="admin-title">Media Library</h1>
          <p className="admin-subtitle">
            Upload, optimize, and manage high-resolution photography, graphics, and responsive article assets.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(e.target.files)}
            accept="image/jpeg,image/png,image/webp,image/avif"
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="admin-btn-primary"
          >
            <UploadCloud size={16} />
            <span>{uploading ? "Uploading..." : "Upload New Image"}</span>
          </button>
        </div>
      </header>

      {/* Upload Notification / Drag and Drop Area */}
      <div
        className="admin-card-panel border-dashed border-2 border-slate-300 hover:border-[#C99A2E] transition-colors p-6 text-center cursor-pointer bg-slate-50/50"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFileUpload(e.dataTransfer.files);
        }}
      >
        <div className="flex flex-col items-center justify-center gap-2">
          <UploadCloud size={32} className="text-[#C99A2E]" />
          <p className="font-semibold text-slate-800 text-sm">
            Drag and drop high-res images here, or click to browse
          </p>
          <p className="text-xs text-slate-500">
            Supports JPEG, PNG, WebP, AVIF up to 10MB. Automatic responsive WebP generation.
          </p>
          {uploadProgress && (
            <div className="mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">
              {uploadProgress}
            </div>
          )}
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadMedia(searchQuery);
          }}
          className="flex-1 max-w-md relative"
        >
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search images by filename, alt text, or credit..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#C99A2E]"
          />
        </form>

        <div className="flex items-center gap-3 justify-between sm:justify-end">
          <div className="text-xs font-semibold text-slate-500">
            Showing {items.length} image{items.length === 1 ? "" : "s"}
          </div>

          <div className="flex items-center border border-slate-200 rounded-lg bg-slate-50 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === "grid"
                  ? "bg-white text-slate-900 shadow-xs font-bold"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              title="Grid View"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === "table"
                  ? "bg-white text-slate-900 shadow-xs font-bold"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              title="Editorial Table View"
            >
              <List size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Grid or Table of Media */}
      {loading ? (
        <LoadingState label="Indexing editorial media archive..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : items.length === 0 ? (
        searchQuery ? (
          <EmptyState
            icon={Search}
            badge="SEARCH RESULTS"
            title="No media assets match your query"
            description={`We couldn't find any images matching "${searchQuery}". Check the spelling, try broader keywords, or clear the search query.`}
            actionLabel="Reset Search Filter"
            onAction={() => {
              setSearchQuery("");
              loadMedia("");
            }}
            actionIcon={RotateCcw}
            secondaryActionLabel="Upload New Photo"
            onSecondaryAction={() => fileInputRef.current?.click()}
          />
        ) : (
          <EmptyState
            icon={ImageIcon}
            badge="MEDIA ASSET VAULT"
            title="No media uploaded yet"
            description="Your newsroom media library is currently empty. Upload high-resolution photographs, vectors, diagrams, and cover graphics to use across your publications."
            actionLabel="Upload First Photo"
            onAction={() => fileInputRef.current?.click()}
            actionIcon={UploadCloud}
          />
        )
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {items.map((media) => (
            <div
              key={media.id}
              onClick={() => handleOpenDetails(media)}
              className="group relative bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-[#C99A2E] hover:shadow-md transition-all cursor-pointer flex flex-col"
            >
              <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden flex items-center justify-center">
                <img
                  src={media.url}
                  alt={media.alt_text || media.filename}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <span className="p-1.5 bg-white/90 text-slate-800 rounded-full text-xs font-semibold">
                    <Eye size={14} />
                  </span>
                </div>
              </div>

              <div className="p-2.5 flex flex-col justify-between flex-1">
                <p className="text-xs font-medium text-slate-800 truncate" title={media.original_filename || media.filename}>
                  {media.original_filename || media.filename}
                </p>
                <div className="flex items-center justify-between text-[11px] text-slate-500 mt-1">
                  <span>{media.width && media.height ? `${media.width}×${media.height}` : "IMG"}</span>
                  <span>{formatBytes(media.file_size)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="admin-card-panel p-0 overflow-hidden">
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>Asset</th>
                  <th>Filename & Title</th>
                  <th>Dimensions & Type</th>
                  <th>File Size</th>
                  <th>Alt Text / Credit</th>
                  <th>Uploaded Date</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((media) => (
                  <tr key={media.id}>
                    <td>
                      <div
                        onClick={() => handleOpenDetails(media)}
                        className="w-12 h-10 rounded-md bg-slate-100 overflow-hidden border border-slate-200 cursor-pointer relative group flex items-center justify-center"
                      >
                        <img
                          src={media.url}
                          alt={media.alt_text || media.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="admin-table-title-cell">
                        <button
                          type="button"
                          onClick={() => handleOpenDetails(media)}
                          className="text-left font-bold text-slate-900 text-xs hover:text-[#C99A2E] truncate max-w-xs block cursor-pointer"
                        >
                          {media.original_filename || media.filename}
                        </button>
                        <span className="admin-table-story-slug truncate max-w-xs">{media.filename}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-mono font-medium text-slate-700">
                          {media.width && media.height ? `${media.width} × ${media.height} px` : "Dimensions N/A"}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono uppercase">
                          {media.mime_type || "image/jpeg"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                        {formatBytes(media.file_size)}
                      </span>
                    </td>
                    <td>
                      <div className="text-xs text-slate-600 max-w-xs truncate">
                        {media.credit ? (
                          <span className="font-medium text-slate-800">By {media.credit}</span>
                        ) : media.alt_text ? (
                          <span>{media.alt_text}</span>
                        ) : (
                          <span className="italic text-slate-400">No caption/credit</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
                        {formatDate(media.created_at)}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="admin-row-actions">
                        <button
                          type="button"
                          onClick={() => handleOpenDetails(media)}
                          className="action-icon-btn"
                          title="View & Edit Asset Details"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => copyUrl(media.url, media.id)}
                          className="action-icon-btn"
                          title="Copy Asset CDN URL"
                        >
                          {copiedId === media.id ? (
                            <Check size={14} className="text-emerald-600" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(media.id)}
                          className="action-icon-btn delete"
                          title="Delete Asset"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail / Edit Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto flex flex-col border border-slate-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <ImageIcon size={18} className="text-[#C99A2E]" />
                <h3 className="font-bold text-slate-900 text-base">Media Asset Details</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedMedia(null)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Image Preview */}
              <div className="flex flex-col gap-3">
                <div className="bg-slate-900 rounded-lg overflow-hidden border border-slate-800 aspect-video flex items-center justify-center relative">
                  <img
                    src={selectedMedia.url}
                    alt={selectedMedia.alt_text || selectedMedia.filename}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>

                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs flex flex-col gap-1.5">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Dimensions:</span>
                    <span className="font-semibold text-slate-800">
                      {selectedMedia.width} × {selectedMedia.height} px
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">File Size:</span>
                    <span className="font-semibold text-slate-800">
                      {formatBytes(selectedMedia.file_size)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">MIME Type:</span>
                    <span className="font-semibold text-slate-800">{selectedMedia.mime_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Uploaded:</span>
                    <span className="font-semibold text-slate-800">
                      {formatDate(selectedMedia.created_at)}
                    </span>
                  </div>
                </div>

                {/* Quick Copy URL */}
                <button
                  type="button"
                  onClick={() => copyUrl(selectedMedia.url, selectedMedia.id)}
                  className="flex items-center justify-center gap-2 py-2 px-3 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  {copiedId === selectedMedia.id ? (
                    <>
                      <Check size={14} className="text-emerald-600" />
                      <span className="text-emerald-700">URL Copied to Clipboard!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      <span>Copy Public Image URL</span>
                    </>
                  )}
                </button>
              </div>

              {/* Right: Metadata Form */}
              <div className="flex flex-col justify-between">
                {isEditing ? (
                  <form onSubmit={handleSaveEdit} className="flex flex-col gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                        Alt Text (Accessibility)
                      </label>
                      <input
                        type="text"
                        value={editForm.alt_text}
                        onChange={(e) => setEditForm({ ...editForm, alt_text: e.target.value })}
                        placeholder="Describe the image content..."
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-xs focus:outline-none focus:border-[#C99A2E]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                        Caption
                      </label>
                      <textarea
                        rows={2}
                        value={editForm.caption}
                        onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                        placeholder="Editorial caption displayed below the photo..."
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-xs focus:outline-none focus:border-[#C99A2E]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                        Photo Credit / Source
                      </label>
                      <input
                        type="text"
                        value={editForm.credit}
                        onChange={(e) => setEditForm({ ...editForm, credit: e.target.value })}
                        placeholder="e.g. Tendai Moyo / ZimSmart Newsroom"
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-xs focus:outline-none focus:border-[#C99A2E]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                        Internal Description
                      </label>
                      <textarea
                        rows={2}
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        placeholder="Editorial notes..."
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-xs focus:outline-none focus:border-[#C99A2E]"
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <button
                        type="submit"
                        disabled={savingEdit}
                        className="flex-1 bg-[#111318] text-white py-1.5 px-3 rounded-lg text-xs font-semibold hover:bg-slate-800 transition-colors"
                      >
                        {savingEdit ? "Saving..." : "Save Metadata"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="py-1.5 px-3 border border-slate-300 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Alt Text
                      </span>
                      <p className="text-xs text-slate-800 font-medium mt-0.5">
                        {selectedMedia.alt_text || <span className="italic text-slate-400">None set</span>}
                      </p>
                    </div>

                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Caption
                      </span>
                      <p className="text-xs text-slate-800 font-medium mt-0.5">
                        {selectedMedia.caption || <span className="italic text-slate-400">None set</span>}
                      </p>
                    </div>

                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Photo Credit
                      </span>
                      <p className="text-xs text-slate-800 font-medium mt-0.5">
                        {selectedMedia.credit || <span className="italic text-slate-400">None set</span>}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="flex items-center justify-center gap-1.5 py-1.5 px-3 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      <Edit2 size={13} />
                      <span>Edit Metadata</span>
                    </button>
                  </div>
                )}

                {/* Modal Footer / Delete */}
                <div className="border-t border-slate-200 pt-4 mt-4 flex items-center justify-between">
                  <a
                    href={selectedMedia.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-[#C99A2E] hover:underline flex items-center gap-1"
                  >
                    <span>Open in new tab</span>
                    <ExternalLink size={12} />
                  </a>

                  <button
                    type="button"
                    onClick={() => handleDelete(selectedMedia.id)}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-800 flex items-center gap-1 p-1"
                  >
                    <Trash2 size={13} />
                    <span>Delete Asset</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
